/**
 * The game's front end (spec 08 §1, §4): the title's menu and its screens, and the pause menu in play.
 * P, Esc or a pad's START pauses; P or START again goes on; Q while paused quits to the title at once,
 * as on the C64. Everything else in a menu goes through `Menus`.
 */

import type { Keyboard } from '../../input';
import { MenuControls } from '../../input/menuInput';
import type { GameView } from '../../render/game/GameView';
import type { SettingsStore } from '../../settings';
import { type MenuColours, Menus } from '../../ui/menu';
import { RELEASES, VERSION } from '../version';
import type { Session } from './session';

export class FrontEnd {
  readonly menus: Menus;
  private readonly controls: MenuControls;

  constructor(
    private readonly session: Session,
    private readonly store: SettingsStore,
    private readonly keys: Keyboard,
    view: GameView,
    /** The menus' colours now (they follow the look: the C64's own in the 1987 look). */
    private readonly colours: () => MenuColours,
  ) {
    this.menus = new Menus(store, RELEASES, VERSION);
    this.controls = new MenuControls(window, () => view.pipeline.viewRect);
    if (session.mode === 'title') this.menus.open('title');
  }

  /** One 50 Hz frame, before the session's tick (it reads this frame's taps, so before `endFrame`). */
  frame(): void {
    const s = this.session;
    const m = this.menus;
    this.controls.poll();
    if (s.mode === 'title') {
      if (m.screen === null && !s.transition.active) m.open('title');
    } else if (m.screen === 'title') {
      m.close();
    }
    // While the controls screen waits for a key, every key is for it (P, Q, Esc included).
    if (s.mode !== 'title' && !s.transition.active && !m.capturing) this.pauseKeys();
    if (m.screen === null) return;
    const action = m.update(this.controls.read(this.keys, this.store.get().keys));
    if (action?.type === 'start') {
      m.close();
      s.start([true, action.players === 2], true);
    } else if (action?.type === 'resume') {
      m.close();
      s.paused = false;
    } else if (action?.type === 'quit') {
      m.close();
      s.quit();
    }
  }

  private pauseKeys(): void {
    const s = this.session;
    const toggle = this.keys.take('KeyP') || this.controls.startPressed();
    if (!s.paused && (toggle || this.keys.take('Escape'))) {
      s.paused = true;
      this.menus.open('pause');
    } else if (s.paused && toggle) {
      s.paused = false;
      this.menus.close();
    } else if (s.paused && this.menus.screen === 'pause' && this.keys.take('KeyQ')) {
      this.menus.close();
      s.quit();
    }
  }

  /** Draw the open menu over the HUD. */
  draw(ctx: CanvasRenderingContext2D): void {
    this.menus.draw(ctx, this.colours());
  }
}
